import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, Grid, Chip, Stack, Skeleton, Alert, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip,
  CartesianGrid, Legend,
} from 'recharts';
import { pitchSplitsApi } from '../../services/apiService';
import { PITCH_NAMES, PITCH_COLORS } from '../../utils/pitchTypes';
import PitchSequencingCard from '../PitchSequencingCard';
import PitchTunnelingCard from '../PitchTunnelingCard';

// ============================================================================
// ARSENAL TAB — Pitcher repertoire deep dive
//   - Per-pitch Stuff+ grade cards (usage/counts derived from deduplicated
//     pitchEvents, never pitch_types.count which drifts on re-ingestion)
//   - Movement plot vs league corpus (/lab/corpus blob)
//   - Usage by count situation and batter hand
//   - Velocity trend by game
//   - Sequencing + tunneling promoted from Pitch Analysis
// ============================================================================

// Same thresholds as Pitch Lab
const gradeFor = (stuff) => {
  if (stuff >= 130) return { label: 'Elite', color: '#d32f2f' };
  if (stuff >= 120) return { label: 'Plus-Plus', color: '#f57c00' };
  if (stuff >= 110) return { label: 'Plus', color: '#fbc02d' };
  if (stuff >= 95) return { label: 'Average', color: '#7cb342' };
  if (stuff >= 85) return { label: 'Below Avg', color: '#0288d1' };
  return { label: 'Fringe', color: '#78909c' };
};

const fmt1 = (v) => (v == null || isNaN(v) ? '—' : Number(v).toFixed(1));
const fmt0 = (v) => (v == null || isNaN(v) ? '—' : Math.round(v));

// ────────────────────────────────────────────────────────────────────────────
// Movement plot: |HB| (x) vs IVB (y). League dots (faint, per type) + this
// pitcher's arsenal (large ringed dots with labels).
// ────────────────────────────────────────────────────────────────────────────
const ArsenalMovementPlot = ({ leagueRows, arsenal }) => {
  const theme = useTheme();
  const W = 460, H = 380, PAD = 40;
  const own = arsenal.filter(p => p.ivb != null && p.hb != null);
  const league = leagueRows.filter(r => r.ivb != null && r.hb != null);
  const xs = league.map(r => Math.abs(r.hb)).concat(own.map(p => Math.abs(p.hb)));
  const ys = league.map(r => r.ivb).concat(own.map(p => p.ivb));
  if (xs.length === 0) return null;
  const xMin = Math.min(0, ...xs) - 2, xMax = Math.max(...xs, 4) + 2;
  const yMin = Math.min(...ys, -5) - 2, yMax = Math.max(...ys, 5) + 2;
  const px = (v) => PAD + ((v - xMin) / (xMax - xMin)) * (W - PAD * 2);
  const py = (v) => H - PAD - ((v - yMin) / (yMax - yMin)) * (H - PAD * 2);
  const gridColor = alpha(theme.palette.text.primary, 0.1);
  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {yMin < 0 && yMax > 0 && (
          <line x1={PAD} x2={W - PAD} y1={py(0)} y2={py(0)} stroke={gridColor} strokeDasharray="4 4" />
        )}
        <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} stroke={gridColor} />
        <line x1={PAD} x2={PAD} y1={PAD} y2={H - PAD} stroke={gridColor} />
        <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary}>
          Horizontal Break (in)
        </text>
        <text x={12} y={H / 2} textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary}
          transform={`rotate(-90 12 ${H / 2})`}>
          Induced Vertical Break (in)
        </text>
        {/* league backdrop */}
        {league.map((r, i) => (
          <circle key={i} cx={px(Math.abs(r.hb))} cy={py(r.ivb)} r={2.5}
            fill={alpha(PITCH_COLORS[r.pt] || '#888', 0.18)} />
        ))}
        {/* this pitcher's arsenal */}
        {own.map((p) => (
          <g key={p.code}>
            <circle cx={px(Math.abs(p.hb))} cy={py(p.ivb)} r={9}
              fill={PITCH_COLORS[p.code] || '#888'}
              stroke={theme.palette.background.paper} strokeWidth={2.5} />
            <circle cx={px(Math.abs(p.hb))} cy={py(p.ivb)} r={13}
              fill="none" stroke={PITCH_COLORS[p.code] || '#888'} strokeWidth={1.5} strokeDasharray="3 3" />
            <text x={px(Math.abs(p.hb))} y={py(p.ivb) - 17} textAnchor="middle" fontSize="11"
              fontWeight="700" fill={theme.palette.text.primary}>
              {p.code}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Horizontal stacked usage bar for a situation row
// ────────────────────────────────────────────────────────────────────────────
const UsageBar = ({ label, mix, total, order }) => {
  const theme = useTheme();
  if (!total) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" fontWeight={700}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{total} pitches</Typography>
      </Box>
      <Box sx={{ display: 'flex', height: 18, borderRadius: 2, overflow: 'hidden',
        bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
        {order.map(code => {
          const n = mix[code] || 0;
          if (!n) return null;
          const pct = (n / total) * 100;
          return (
            <Tooltip key={code} title={`${PITCH_NAMES[code] || code}: ${pct.toFixed(1)}% (${n})`} arrow>
              <Box sx={{ width: `${pct}%`, bgcolor: PITCH_COLORS[code] || '#888',
                display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
                {pct >= 12 && (
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.62rem', lineHeight: 1 }}>
                    {code} {Math.round(pct)}%
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main tab
// ────────────────────────────────────────────────────────────────────────────
const ArsenalTab = ({ player, pitchData, pitchEvents, year, gameTypeFilter }) => {
  const theme = useTheme();
  const [corpus, setCorpus] = useState(null);
  const [corpusLoading, setCorpusLoading] = useState(true);
  const [veloView, setVeloView] = useState('primary');

  useEffect(() => {
    let cancelled = false;
    setCorpusLoading(true);
    pitchSplitsApi.getLabCorpus(year)
      .then(res => { if (!cancelled) setCorpus(res?.pitches ? res : res?.data || null); })
      .catch(() => { if (!cancelled) setCorpus(null); })
      .finally(() => { if (!cancelled) setCorpusLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  // Filter events by game type when a specific type is selected
  const events = useMemo(() => {
    const evts = pitchEvents || [];
    if (gameTypeFilter && gameTypeFilter !== 'all') {
      return evts.filter(e => (e.gt || 'R') === gameTypeFilter);
    }
    return evts;
  }, [pitchEvents, gameTypeFilter]);

  // True usage counts from deduplicated events
  const usage = useMemo(() => {
    const counts = {};
    let total = 0;
    for (const e of events) {
      const pt = e.pitchType || 'UN';
      counts[pt] = (counts[pt] || 0) + 1;
      total++;
    }
    return { counts, total };
  }, [events]);

  // Arsenal rows: merge pitchData.pitchTypes aggregates with event-derived usage
  const arsenal = useMemo(() => {
    const types = pitchData?.pitchTypes || {};
    const rows = [];
    for (const [code, pt] of Object.entries(types)) {
      if (code === 'UN') continue;
      const evtCount = usage.counts[code] || 0;
      // Skip trace pitch types (misclassifications) with no meaningful usage
      if (usage.total > 0 && evtCount === 0) continue;
      rows.push({
        code,
        name: PITCH_NAMES[code] || pt.name || code,
        stuffPlus: pt.stuffPlus ?? pitchData?.stuffPlusByPitch?.[code] ?? null,
        velo: pt.avgVelo || null,
        ivb: pt.avgVertBreak ?? null,
        hb: pt.avgHorizBreak ?? null,
        spin: pt.avgSpin || null,
        whiff: pt.whiffRate ?? null,
        csw: pt.cswRate != null ? pt.cswRate * 100 : null,
        count: evtCount,
        usagePct: usage.total > 0 ? (evtCount / usage.total) * 100 : null,
      });
    }
    rows.sort((a, b) => (b.usagePct || 0) - (a.usagePct || 0));
    return rows;
  }, [pitchData, usage]);

  const typeOrder = useMemo(() => arsenal.map(p => p.code), [arsenal]);

  // League corpus rows limited to this pitcher's pitch types
  const leagueRows = useMemo(() => {
    if (!corpus?.pitches) return [];
    const mine = new Set(typeOrder);
    return corpus.pitches.filter(r => mine.has(r.pt));
  }, [corpus, typeOrder]);

  // League percentile of each pitch's Stuff+ within its type
  const stuffPercentiles = useMemo(() => {
    if (!corpus?.pitches) return {};
    const byType = {};
    for (const r of corpus.pitches) {
      if (r.stuffPlus == null) continue;
      (byType[r.pt] = byType[r.pt] || []).push(r.stuffPlus);
    }
    const out = {};
    for (const p of arsenal) {
      const pool = byType[p.code];
      if (!pool || pool.length < 10 || p.stuffPlus == null) continue;
      const below = pool.filter(v => v < p.stuffPlus).length;
      const equal = pool.filter(v => v === p.stuffPlus).length;
      out[p.code] = Math.round(((below + 0.5 * equal) / pool.length) * 100);
    }
    return out;
  }, [corpus, arsenal]);

  // Usage by count situation + batter hand.
  // The API's balls/strikes are POST-pitch, so the pre-pitch count is the
  // previous pitch's post-pitch count within the same at-bat (0-0 on AB start).
  const situations = useMemo(() => {
    const mk = () => ({ mix: {}, total: 0 });
    const s = {
      first: mk(), ahead: mk(), behind: mk(), even: mk(), twoStrikes: mk(), threeBalls: mk(),
      vsL: mk(), vsR: mk(),
    };
    const add = (bucket, pt) => { bucket.mix[pt] = (bucket.mix[pt] || 0) + 1; bucket.total++; };
    let balls = 0, strikes = 0, curGame = null, hasCounts = false;
    for (const e of events) {
      const pt = e.pitchType || 'UN';
      if (e.gameId !== curGame) { curGame = e.gameId; balls = 0; strikes = 0; }
      // Bucket by pre-pitch count (carried from previous pitch)
      if (e.balls != null && e.strikes != null) {
        hasCounts = true;
        if (balls === 0 && strikes === 0) add(s.first, pt);
        else if (strikes > balls) add(s.ahead, pt);
        else if (balls > strikes) add(s.behind, pt);
        else add(s.even, pt);
        if (strikes === 2) add(s.twoStrikes, pt);
        if (balls === 3) add(s.threeBalls, pt);
      }
      if (e.opponentHand === 'L') add(s.vsL, pt);
      else if (e.opponentHand === 'R') add(s.vsR, pt);
      // Carry post-pitch count forward; reset at end of at-bat
      if (e.isLastPitch !== false) { balls = 0; strikes = 0; }
      else { balls = e.balls ?? balls; strikes = e.strikes ?? strikes; }
    }
    s.hasCounts = hasCounts;
    return s;
  }, [events]);

  const hasCountData = situations.hasCounts && (situations.first.total + situations.ahead.total +
    situations.behind.total + situations.even.total > 0);

  // Velocity trend by game (top 3 pitch types by usage)
  const veloTrend = useMemo(() => {
    const topTypes = typeOrder.slice(0, veloView === 'primary' ? 1 : 3);
    if (topTypes.length === 0) return { data: [], types: [] };
    const byGame = new Map();
    for (const e of events) {
      const pt = e.pitchType;
      if (!topTypes.includes(pt) || !e.velocity || e.velocity <= 0) continue;
      const gKey = e.gameDate || e.gameId;
      if (!gKey) continue;
      if (!byGame.has(gKey)) byGame.set(gKey, { date: e.gameDate, opp: e.opponent, sums: {}, counts: {} });
      const g = byGame.get(gKey);
      g.sums[pt] = (g.sums[pt] || 0) + e.velocity;
      g.counts[pt] = (g.counts[pt] || 0) + 1;
    }
    const data = Array.from(byGame.values())
      .filter(g => g.date)
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map(g => {
        const row = { date: g.date?.slice(5), fullDate: g.date, opp: g.opp };
        for (const pt of topTypes) {
          if (g.counts[pt] >= 3) row[pt] = +(g.sums[pt] / g.counts[pt]).toFixed(1);
        }
        return row;
      });
    return { data, types: topTypes };
  }, [events, typeOrder, veloView]);

  if (!pitchData?.pitchTypes || arsenal.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 3 }}>
        No pitch arsenal data available for this pitcher in {year}.
      </Alert>
    );
  }

  const overallStuff = pitchData.stuffPlus ?? null;
  const overallGrade = overallStuff != null ? gradeFor(overallStuff) : null;
  const cardSx = { border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, height: '100%' };

  return (
    <Grid container spacing={2}>
      {/* ── Arsenal overview: per-pitch grade cards ──────────────────────── */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ ...cardSx, height: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>Arsenal</Typography>
            {overallStuff != null && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Arsenal Stuff+</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: overallGrade.color }}>{overallStuff}</Typography>
                <Chip size="small" label={overallGrade.label}
                  sx={{ bgcolor: alpha(overallGrade.color, 0.15), color: overallGrade.color, fontWeight: 700 }} />
              </Stack>
            )}
          </Box>
          <Grid container spacing={1.5}>
            {arsenal.map((p) => {
              const grade = p.stuffPlus != null ? gradeFor(p.stuffPlus) : null;
              const pct = stuffPercentiles[p.code];
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={p.code}>
                  <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 2.5, p: 1.5,
                    borderTop: `3px solid ${PITCH_COLORS[p.code] || '#888'}`, height: '100%',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.usagePct != null ? `${p.usagePct.toFixed(1)}% usage` : ''}{p.count ? ` · ${p.count} pitches` : ''}
                        </Typography>
                      </Box>
                      {p.stuffPlus != null && (
                        <Tooltip title={pct != null ? `${pct}th percentile among MLB ${p.name}s` : 'Stuff+'} arrow>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color: grade.color, lineHeight: 1 }}>
                              {p.stuffPlus}
                            </Typography>
                            <Typography variant="caption" sx={{ color: grade.color, fontWeight: 700 }}>
                              {grade.label}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    <Grid container spacing={0.5} sx={{ mt: 0.25 }}>
                      {[
                        ['Velo', p.velo != null ? `${fmt1(p.velo)}` : '—'],
                        ['IVB', p.ivb != null ? `${fmt1(p.ivb)}"` : '—'],
                        ['HB', p.hb != null ? `${fmt1(p.hb)}"` : '—'],
                        ['Spin', p.spin != null ? `${fmt0(p.spin)}` : '—'],
                        ['Whiff%', p.whiff != null ? `${fmt1(p.whiff)}%` : '—'],
                        ['CSW%', p.csw != null ? `${fmt1(p.csw)}%` : '—'],
                      ].map(([label, val]) => (
                        <Grid item xs={4} key={label}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.62rem' }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {val}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      </Grid>

      {/* ── Movement plot vs league ──────────────────────────────────────── */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={cardSx}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Movement vs League</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Faint dots are every qualified MLB pitcher's version of the same pitch types.
          </Typography>
          {corpusLoading ? (
            <Skeleton variant="rounded" height={320} />
          ) : leagueRows.length > 0 || arsenal.some(p => p.ivb != null) ? (
            <ArsenalMovementPlot leagueRows={leagueRows} arsenal={arsenal} />
          ) : (
            <Alert severity="info">No movement data available.</Alert>
          )}
        </Card>
      </Grid>

      {/* ── Usage by situation ───────────────────────────────────────────── */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={cardSx}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Pitch Mix by Situation</Typography>
          {hasCountData ? (
            <>
              <UsageBar label="First Pitch (0-0)" {...situations.first} order={typeOrder} />
              <UsageBar label="Pitcher Ahead" {...situations.ahead} order={typeOrder} />
              <UsageBar label="Pitcher Behind" {...situations.behind} order={typeOrder} />
              <UsageBar label="Even Count" {...situations.even} order={typeOrder} />
              <UsageBar label="Two Strikes" {...situations.twoStrikes} order={typeOrder} />
              <UsageBar label="Three Balls" {...situations.threeBalls} order={typeOrder} />
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 1 }}>Count-state data not available for this season.</Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>vs Batter Hand</Typography>
            <UsageBar label="vs LHB" {...situations.vsL} order={typeOrder} />
            <UsageBar label="vs RHB" {...situations.vsR} order={typeOrder} />
          </Box>
        </Card>
      </Grid>

      {/* ── Velocity trend ───────────────────────────────────────────────── */}
      {veloTrend.data.length >= 2 && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{ ...cardSx, height: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>Velocity Trend</Typography>
              <ToggleButtonGroup size="small" exclusive value={veloView} onChange={(e, v) => v && setVeloView(v)}>
                <ToggleButton value="primary" sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem' }}>Primary Pitch</ToggleButton>
                <ToggleButton value="top3" sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem' }}>Top 3 Pitches</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={veloTrend.data} margin={{ top: 5, right: 15, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{ fontSize: 12 }}
                  tickFormatter={(v) => v.toFixed(0)} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12,
                  }}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload;
                    return row ? `${row.fullDate}${row.opp ? ` · ${row.opp}` : ''}` : label;
                  }}
                  formatter={(value, name) => [`${value} mph`, PITCH_NAMES[name] || name]}
                />
                {veloTrend.types.length > 1 && <Legend formatter={(v) => PITCH_NAMES[v] || v} />}
                {veloTrend.types.map(pt => (
                  <Line key={pt} type="monotone" dataKey={pt} stroke={PITCH_COLORS[pt] || '#888'}
                    strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      )}

      {/* ── Per-pitch results table ──────────────────────────────────────── */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ ...cardSx, height: 'auto' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Arsenal Detail</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Pitch</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Usage</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Stuff+</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>%ile</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Velo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>IVB</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>HB</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Spin</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Whiff%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>CSW%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {arsenal.map(p => {
                  const grade = p.stuffPlus != null ? gradeFor(p.stuffPlus) : null;
                  return (
                    <TableRow key={p.code} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PITCH_COLORS[p.code] || '#888' }} />
                          <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{p.usagePct != null ? `${p.usagePct.toFixed(1)}%` : '—'}</TableCell>
                      <TableCell align="right">
                        {p.stuffPlus != null ? (
                          <Typography variant="body2" fontWeight={800} sx={{ color: grade.color }}>{p.stuffPlus}</Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">{stuffPercentiles[p.code] != null ? `${stuffPercentiles[p.code]}th` : '—'}</TableCell>
                      <TableCell align="right">{fmt1(p.velo)}</TableCell>
                      <TableCell align="right">{p.ivb != null ? `${fmt1(p.ivb)}"` : '—'}</TableCell>
                      <TableCell align="right">{p.hb != null ? `${fmt1(p.hb)}"` : '—'}</TableCell>
                      <TableCell align="right">{fmt0(p.spin)}</TableCell>
                      <TableCell align="right">{p.whiff != null ? `${fmt1(p.whiff)}%` : '—'}</TableCell>
                      <TableCell align="right">{p.csw != null ? `${fmt1(p.csw)}%` : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>

      {/* ── Sequencing + Tunneling (promoted from Pitch Analysis) ────────── */}
      {player?.team && player?.name && (
        <>
          <Grid item xs={12} md={6}>
            <PitchSequencingCard
              team={player.team}
              playerName={player.name}
              season={year}
              perspective="pitcher"
              gameType={gameTypeFilter !== 'all' ? gameTypeFilter : undefined}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PitchTunnelingCard
              team={player.team}
              playerName={player.name}
              season={year}
              perspective="pitcher"
              gameType={gameTypeFilter !== 'all' ? gameTypeFilter : undefined}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ArsenalTab;
